import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Alert, Chip,
  Divider, CircularProgress, Select, MenuItem, FormControl,
  InputLabel, Menu, useMediaQuery, useTheme
} from '@mui/material';
import { Add, Delete, ArrowBack, Check, Refresh, HourglassEmpty, Cancel, RadioButtonUnchecked, FileDownload, Upload } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const VL_COLORS = [
  { light: '#fff8e1', dark: '#f9a825' }, { light: '#e3f2fd', dark: '#1976d2' },
  { light: '#e8f5e9', dark: '#388e3c' }, { light: '#f3e5f5', dark: '#7b1fa2' },
  { light: '#eceff1', dark: '#546e7a' }, { light: '#fce4ec', dark: '#c2185b' },
  { light: '#e0f7fa', dark: '#0097a7' }
];

const vlColorCache = {};
let colorIndex = 2;

const getVLColors = (vlName) => {
  if (!vlName) return { light: '#f5f5f5', dark: '#9e9e9e' };
  const name = vlName.toLowerCase().trim();
  if (name === 'openers' || name === 'op') return VL_COLORS[0];
  if (name === 'op+null' || name === 'opnull' || name.includes('op+null')) return VL_COLORS[1];
  if (vlColorCache[name]) return vlColorCache[name];
  const colors = VL_COLORS[colorIndex % VL_COLORS.length];
  vlColorCache[name] = colors;
  colorIndex++;
  return colors;
};

function DemDatabaseDetail() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const { id } = useParams();
  const navigate = useNavigate();
  const [database, setDatabase] = useState(null);
  const [sends, setSends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [domainDialogOpen, setDomainDialogOpen] = useState(false);
  const [vlDialogOpen, setVlDialogOpen] = useState(false);
  const [newDomain, setNewDomain] = useState({ domain: '', virtual_list_id: '', sender_id: '' });
  const [newVl, setNewVl] = useState({ name: '', description: '' });
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [localVolumes, setLocalVolumes] = useState({});
  const [volumeTimeouts, setVolumeTimeouts] = useState({});
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const [statusMenuEntryId, setStatusMenuEntryId] = useState(null);
  const [vlMaildomeDialogOpen, setVlMaildomeDialogOpen] = useState(false);
  const [selectedVl, setSelectedVl] = useState(null);
  const [vlMaildomeIds, setVlMaildomeIds] = useState([]);
  const [newVlMaildomeId, setNewVlMaildomeId] = useState('');
  const [editVlName, setEditVlName] = useState('');
  const [optoutDialogOpen, setOptoutDialogOpen] = useState(false);
  const [optoutText, setOptoutText] = useState('');
  const [optoutLoading, setOptoutLoading] = useState(false);
  const [optoutResult, setOptoutResult] = useState(null);

  useEffect(() => { fetchDatabase(); }, [id]);
  useEffect(() => { if (database) fetchSends(); }, [database, selectedYear, selectedMonth]);
  useEffect(() => { if (!database) return; const interval = setInterval(() => { fetchSends(); }, 30000); return () => clearInterval(interval); }, [database, selectedYear, selectedMonth]);

  const fetchDatabase = async () => {
    try { const res = await axios.get(`/admin/dem/databases/${id}`); setDatabase(res.data.data); }
    catch (error) { console.error('Error:', error); setMessage({ type: 'error', text: 'Failed to load database' }); }
    finally { setLoading(false); }
  };

  const fetchSends = async () => {
    try { const res = await axios.get(`/admin/dem/databases/${id}/sends`, { params: { year: selectedYear, month: selectedMonth } }); setSends(res.data.data || []); setLocalVolumes({}); }
    catch (error) { console.error('Error:', error); }
  };

  const handleAddDomain = async () => {
    if (!newDomain.domain.trim()) return;
    try { await axios.post(`/admin/dem/databases/${id}/domains`, newDomain); setDomainDialogOpen(false); setNewDomain({ domain: '', virtual_list_id: '', sender_id: '' }); fetchDatabase(); }
    catch (error) { setMessage({ type: 'error', text: 'Error adding domain' }); }
  };

  const handleUpdateDomainVL = async (domainId, virtualListId) => {
    try { await axios.put(`/admin/dem/domains/${domainId}`, { virtual_list_id: virtualListId }); fetchDatabase(); }
    catch (error) { setMessage({ type: 'error', text: 'Error updating domain' }); }
  };

  const handleDeleteDomain = async (domainId) => {
    try { await axios.delete(`/admin/dem/domains/${domainId}`); fetchDatabase(); }
    catch (error) { setMessage({ type: 'error', text: 'Error deleting domain' }); }
  };

  const handleAddVl = async () => {
    if (!newVl.name.trim()) return;
    try { await axios.post(`/admin/dem/databases/${id}/virtual-lists`, newVl); setVlDialogOpen(false); setNewVl({ name: '', description: '' }); fetchDatabase(); }
    catch (error) { setMessage({ type: 'error', text: 'Error adding virtual list' }); }
  };

  const handleImportOptout = async () => {
    const emails = optoutText.split(/[\n,;]+/).map(e => e.trim()).filter(e => e.length > 0);
    if (emails.length === 0) return;
    setOptoutLoading(true);
    setOptoutResult(null);
    try {
      const res = await axios.post(`/admin/dem/databases/${id}/import-optout`, { emails });
      setOptoutResult(res.data.data);
    } catch (error) {
      setOptoutResult({ error: error.response?.data?.error || 'Error during import' });
    } finally {
      setOptoutLoading(false);
    }
  };


  const handleDeleteVl = async (vlId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this Virtual List?')) return;
    try { await axios.delete(`/admin/dem/virtual-lists/${vlId}`); fetchDatabase(); }
    catch (error) { setMessage({ type: 'error', text: 'Error deleting virtual list' }); }
  };

  const handleVolumeChange = (entryId, value) => {
    setLocalVolumes(prev => ({ ...prev, [entryId]: value }));
    if (volumeTimeouts[entryId]) clearTimeout(volumeTimeouts[entryId]);
    const timeout = setTimeout(async () => {
      try { await axios.put(`/admin/dem/calendar/${entryId}/volume`, { volume: parseInt(value) || 0 }); }
      catch (error) { setMessage({ type: 'error', text: 'Error updating volume' }); }
    }, 1000);
    setVolumeTimeouts(prev => ({ ...prev, [entryId]: timeout }));
  };

  const handleOpenStatusMenu = (event, entryId) => { setStatusMenuAnchor(event.currentTarget); setStatusMenuEntryId(entryId); };
  const handleCloseStatusMenu = () => { setStatusMenuAnchor(null); setStatusMenuEntryId(null); };

  const handleUpdateStatus = async (newStatus) => {
    try { await axios.put(`/admin/dem/calendar/${statusMenuEntryId}`, { status: newStatus }); setSends(prev => prev.map(s => s.id === statusMenuEntryId ? { ...s, status: newStatus } : s)); handleCloseStatusMenu(); }
    catch (error) { setMessage({ type: 'error', text: 'Error updating status' }); }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'planned': return <Check sx={{ color: '#4caf50', fontSize: 18 }} />;
      case 'paused': return <HourglassEmpty sx={{ color: '#ff9800', fontSize: 18 }} />;
      case 'aborted': return <Cancel sx={{ color: '#f44336', fontSize: 18 }} />;
      default: return <RadioButtonUnchecked sx={{ color: '#9e9e9e', fontSize: 18 }} />;
    }
  };

  const handleOpenVlMaildomeDialog = (vl) => {
    setSelectedVl(vl); setEditVlName(vl.name);
    const ids = vl.maildome_vl_ids ? vl.maildome_vl_ids.split(',').map(s => s.trim()).filter(s => s) : [];
    setVlMaildomeIds(ids); setNewVlMaildomeId(''); setVlMaildomeDialogOpen(true);
  };

  const handleAddVlMaildomeId = () => {
    const num = newVlMaildomeId.trim();
    if (num && !isNaN(num) && !vlMaildomeIds.includes(num)) { setVlMaildomeIds([...vlMaildomeIds, num]); setNewVlMaildomeId(''); }
  };

  const handleRemoveVlMaildomeId = (idToRemove) => setVlMaildomeIds(vlMaildomeIds.filter(id => id !== idToRemove));

  const handleSaveVlMaildomeIds = async () => {
    try {
      const idsString = vlMaildomeIds.join(',');
      if (editVlName !== selectedVl.name) await axios.put(`/admin/dem/virtual-lists/${selectedVl.id}`, { name: editVlName });
      await axios.put(`/admin/dem/virtual-lists/${selectedVl.id}/maildome`, { maildome_vl_ids: idsString || null });
      setVlMaildomeDialogOpen(false); fetchDatabase(); setMessage({ type: 'success', text: 'Virtual List updated' });
    } catch (error) { setMessage({ type: 'error', text: 'Error updating Virtual List' }); }
  };

  const getVolumeForSend = (send) => localVolumes[send.id] !== undefined ? localVolumes[send.id] : send.volume || '';

  const calculateStats = (send) => {
    const volume = parseInt(localVolumes[send.id] !== undefined ? localVolumes[send.id] : send.volume) || 0;
    const payout = parseFloat(send.payout) || 0;
    const leads = send.leads || 0;
    const cost = (volume * 0.0002);
    const revenue = parseFloat(send.revenue) || 0;
    const ecpm = volume > 0 ? (revenue / volume) * 1000 : 0;
    const profit = revenue - cost;
    return { cost: cost.toFixed(1), revenue: revenue.toFixed(1), ecpm: ecpm.toFixed(1), profit: profit.toFixed(1) };
  };

  const handleExport = () => {
    if (sortedSends.length === 0) { setMessage({ type: 'warning', text: 'No data to export' }); return; }
    const headers = ['Date', 'Time', 'Campaign', 'VL', 'Status', 'Volume', 'Cost', 'Clicks', 'Leads', 'Revenue', 'eCPM', 'Profit'];
    const rows = sortedSends.map(send => {
      const stats = calculateStats(send);
      return [send.send_date?.substring(5, 10).replace('-', '/') || '', send.send_time?.substring(0, 5) || '', send.campaign_name || '', send.virtual_list_name || '', send.status || 'pending', getVolumeForSend(send), stats.cost, send.clicks, send.leads, stats.revenue, stats.ecpm, stats.profit];
    });
    rows.push(['TOTAL', '', '', '', '', totals.volume, totals.cost.toFixed(1), totals.clicks, totals.leads, totals.revenue.toFixed(1), '-', totals.profit.toFixed(1)]);
    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a'); link.href = URL.createObjectURL(blob); link.download = `${database.name}_${selectedYear}-${String(selectedMonth).padStart(2, '0')}.csv`; link.click();
  };

  const months = [{ value: 1, label: 'Jan' }, { value: 2, label: 'Feb' }, { value: 3, label: 'Mar' }, { value: 4, label: 'Apr' }, { value: 5, label: 'May' }, { value: 6, label: 'Jun' }, { value: 7, label: 'Jul' }, { value: 8, label: 'Aug' }, { value: 9, label: 'Sep' }, { value: 10, label: 'Oct' }, { value: 11, label: 'Nov' }, { value: 12, label: 'Dec' }];

  if (loading) return <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh"><CircularProgress /></Box>;
  if (!database) return <Box p={3}><Alert severity="error">Database not found</Alert></Box>;

  const sortedSends = [...sends].sort((a, b) => (a.send_date + (a.send_time || '99:99')).localeCompare(b.send_date + (b.send_time || '99:99')));

  const totals = sortedSends.reduce((acc, s) => {
    const volume = parseInt(localVolumes[s.id] !== undefined ? localVolumes[s.id] : s.volume) || 0;
    const leads = s.leads || 0; const cost = volume * 0.0002; const revenue = parseFloat(s.revenue) || 0;
    return { volume: acc.volume + volume, clicks: acc.clicks + (s.clicks || 0), leads: acc.leads + leads, cost: acc.cost + cost, revenue: acc.revenue + revenue, profit: acc.profit + (revenue - cost), opens: acc.opens + (s.maildome_unique_opens || 0), mdClicks: acc.mdClicks + (s.maildome_unique_clicks || 0), unsubs: acc.unsubs + (s.maildome_unsubscribes || 0) };
}, { volume: 0, clicks: 0, leads: 0, cost: 0, revenue: 0, profit: 0, opens: 0, mdClicks: 0, unsubs: 0 });

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <IconButton onClick={() => navigate('/dem/databases')} size="small"><ArrowBack /></IconButton>
        <Typography variant="h5" fontWeight={700} sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem' }, flexGrow: 1 }}>{database.name}</Typography>
        <Button variant="outlined" color="warning" startIcon={<Upload />} onClick={() => setOptoutDialogOpen(true)} size="small">Import Opt-out</Button>
      </Box>

      {message.text && <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage({ type: '', text: '' })}>{message.text}</Alert>}

      {/* VL & Domains Card */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ py: 1.5, px: { xs: 1.5, sm: 2 }, '&:last-child': { pb: 1.5 } }}>
             <Box display="flex" flexDirection="column" gap={2}>
            <Box display="flex" alignItems="flex-start" gap={1} flexWrap="wrap">
              <Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>VL:</Typography>
              {database.virtual_lists?.map((vl) => {
                const colors = getVLColors(vl.name);
                const idCount = vl.maildome_vl_ids ? vl.maildome_vl_ids.split(',').length : 0;
                return <Chip key={vl.id} label={<Box display="flex" alignItems="center" gap={0.5}>{vl.name}{idCount > 0 && <Typography variant="caption" sx={{ opacity: 0.7 }}>({idCount})</Typography>}</Box>} size="small" onClick={() => handleOpenVlMaildomeDialog(vl)} onDelete={(e) => handleDeleteVl(vl.id, e)} sx={{ bgcolor: colors.light, border: `1px solid ${colors.dark}`, cursor: 'pointer', fontSize: '0.7rem' }} />;
              })}
              <IconButton size="small" onClick={() => setVlDialogOpen(true)}><Add sx={{ fontSize: 18 }} /></IconButton>
            </Box>
              <Divider />
            <Box display="flex" alignItems="flex-start" gap={1} flexWrap="wrap">
              <Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Domains:</Typography>
              {database.domains?.length === 0 ? <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>None</Typography> : database.domains?.slice(0, isMobile ? 2 : undefined).map((d) => {
                const colors = getVLColors(d.virtual_list_name);
                return (
                  <Box key={d.id} display="flex" alignItems="center" gap={0.5}>
                    <Chip label={d.domain} size="small" sx={{ bgcolor: d.virtual_list_id ? colors.light : '#f5f5f5', fontSize: '0.65rem', height: 22 }} />
                    {!isMobile && (
                      <Select size="small" value={d.virtual_list_id || ''} onChange={(e) => handleUpdateDomainVL(d.id, e.target.value || null)} displayEmpty sx={{ minWidth: 70, height: 22, fontSize: '0.7rem' }}>
                        <MenuItem value="" sx={{ fontSize: '0.7rem' }}>No VL</MenuItem>
                        {database.virtual_lists?.map(vl => <MenuItem key={vl.id} value={vl.id} sx={{ fontSize: '0.7rem' }}>{vl.name}</MenuItem>)}
                      </Select>
                    )}
                    <IconButton size="small" onClick={() => handleDeleteDomain(d.id)} sx={{ p: 0.25 }}><Delete sx={{ fontSize: 14 }} /></IconButton>
                  </Box>
                );
              })}
              {isMobile && database.domains?.length > 2 && <Chip label={`+${database.domains.length - 2}`} size="small" sx={{ fontSize: '0.65rem', height: 20 }} />}
              <IconButton size="small" onClick={() => setDomainDialogOpen(true)}><Add sx={{ fontSize: 18 }} /></IconButton>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Send Analytics */}
      <Card>
        <CardContent sx={{ py: 1.5, px: { xs: 1, sm: 2 } }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5} flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>Analytics</Typography>
              <IconButton size="small" onClick={fetchSends}><Refresh sx={{ fontSize: 18 }} /></IconButton>
              <IconButton size="small" onClick={handleExport}><FileDownload sx={{ fontSize: 18 }} /></IconButton>
            </Box>
            <Box display="flex" gap={0.5}>
              <FormControl size="small" sx={{ minWidth: 60 }}>
                <Select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} sx={{ fontSize: '0.75rem' }}>
                  {months.map(m => <MenuItem key={m.value} value={m.value} sx={{ fontSize: '0.75rem' }}>{m.label}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 70 }}>
                <Select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} sx={{ fontSize: '0.75rem' }}>
                  {[2024, 2025, 2026].map(y => <MenuItem key={y} value={y} sx={{ fontSize: '0.75rem' }}>{y}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
          </Box>
          <TableContainer sx={{ maxHeight: { xs: 350, sm: 500 } }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell align="center" sx={{ width: 40, p: 0.5 }}>St</TableCell>
                  <TableCell sx={{ width: 45, p: 0.5 }}>Date</TableCell>
                  {!isMobile && <TableCell sx={{ width: 45, p: 0.5 }}>Time</TableCell>}
                  <TableCell sx={{ p: 0.5 }}>Campaign</TableCell>
                  {!isMobile && <TableCell sx={{ width: 70, p: 0.5 }}>VL</TableCell>}
                  <TableCell align="right" sx={{ width: isMobile ? 70 : 90, p: 0.5 }}>Vol</TableCell>
                  {!isTablet && <TableCell align="right" sx={{ width: 55, p: 0.5 }}>Cost</TableCell>}
                  {!isMobile && <TableCell align="right" sx={{ width: 50, p: 0.5 }}>Clk</TableCell>}
                  <TableCell align="right" sx={{ width: 45, p: 0.5 }}>Ld</TableCell>
                  <TableCell align="right" sx={{ width: 60, p: 0.5 }}>Rev</TableCell>
                  {!isTablet && <TableCell align="right" sx={{ width: 55, p: 0.5 }}>eCPM</TableCell>}
                  <TableCell align="right" sx={{ width: 60, p: 0.5 }}>Profit</TableCell>
               {!isTablet && <TableCell align="right" sx={{ width: 55, p: 0.5 }}>Opens</TableCell>}
                  {!isTablet && <TableCell align="right" sx={{ width: 50, p: 0.5 }}>MdClk</TableCell>}
                  {!isTablet && <TableCell align="right" sx={{ width: 50, p: 0.5 }}>Unsub</TableCell>}
 </TableRow>
              </TableHead>
              <TableBody>
                {sortedSends.length === 0 ? (
                  <TableRow><TableCell colSpan={isMobile ? 6 : 12} align="center" sx={{ py: 3 }}><Typography color="text.secondary" sx={{ fontSize: '0.8rem' }}>No sends this month</Typography></TableCell></TableRow>
                ) : (
                  <>
                    {sortedSends.map((send, index) => {
                      const colors = getVLColors(send.virtual_list_name);
                      const prevSend = index > 0 ? sortedSends[index - 1] : null;
                      const showDate = !prevSend || prevSend.send_date !== send.send_date;
                      const dateFormatted = send.send_date ? `${send.send_date.substring(8, 10)}/${send.send_date.substring(5, 7)}` : '-';
                      const stats = calculateStats(send);
                      return (
                        <TableRow key={send.id} hover>
                          <TableCell align="center" sx={{ p: 0.25 }}><IconButton size="small" onClick={(e) => handleOpenStatusMenu(e, send.id)} sx={{ p: 0.25 }}>{getStatusIcon(send.status)}</IconButton></TableCell>
                          <TableCell sx={{ p: 0.5, fontSize: '0.7rem' }}>{showDate ? dateFormatted : ''}</TableCell>
                          {!isMobile && <TableCell sx={{ p: 0.5, fontSize: '0.7rem' }}>{send.send_time?.substring(0, 5) || '-'}</TableCell>}
                          <TableCell sx={{ p: 0.5 }}><Typography sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, fontWeight: 600, maxWidth: { xs: 100, sm: 150 }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{send.campaign_name}</Typography></TableCell>
                          {!isMobile && <TableCell sx={{ p: 0.5 }}><Chip label={send.virtual_list_name || '-'} size="small" sx={{ bgcolor: colors.light, fontSize: '0.6rem', height: 18 }} /></TableCell>}
                          <TableCell align="right" sx={{ p: 0.5 }}><TextField size="small" type="number" value={getVolumeForSend(send)} onChange={(e) => handleVolumeChange(send.id, e.target.value)} sx={{ width: isMobile ? 65 : 80 }} inputProps={{ style: { textAlign: 'right', padding: '3px 6px', fontSize: '0.75rem' } }} /></TableCell>
                          {!isTablet && <TableCell align="right" sx={{ p: 0.5, fontSize: '0.7rem' }}>{stats.cost}</TableCell>}
                          {!isMobile && <TableCell align="right" sx={{ p: 0.5, fontSize: '0.7rem' }}>{send.clicks}</TableCell>}
                          <TableCell align="right" sx={{ p: 0.5, fontSize: '0.7rem' }}><strong>{send.leads}</strong></TableCell>
                          <TableCell align="right" sx={{ p: 0.5, fontSize: '0.7rem' }}>{stats.revenue}</TableCell>
                          {!isTablet && <TableCell align="right" sx={{ p: 0.5, fontSize: '0.7rem' }}>{stats.ecpm}</TableCell>}
                          <TableCell align="right" sx={{ p: 0.5, fontSize: '0.7rem', color: parseFloat(stats.profit) >= 0 ? 'success.main' : 'error.main', fontWeight: 600 }}>{stats.profit}</TableCell>
                          {!isTablet && <TableCell align="right" sx={{ p: 0.5, fontSize: '0.7rem' }}>{send.maildome_unique_opens || '-'}</TableCell>}
                          {!isTablet && <TableCell align="right" sx={{ p: 0.5, fontSize: '0.7rem' }}>{send.maildome_unique_clicks || '-'}</TableCell>}
                          {!isTablet && <TableCell align="right" sx={{ p: 0.5, fontSize: '0.7rem' }}>{send.maildome_unsubscribes || '-'}</TableCell>}
 </TableRow>
                      );
                    })}
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell colSpan={isMobile ? 4 : 5} align="right" sx={{ p: 0.5, fontSize: '0.7rem' }}><strong>TOTAL</strong></TableCell>
                      <TableCell align="right" sx={{ p: 0.5, fontSize: '0.7rem' }}><strong>{totals.volume.toLocaleString()}</strong></TableCell>
                      {!isTablet && <TableCell align="right" sx={{ p: 0.5, fontSize: '0.7rem' }}><strong>{totals.cost.toFixed(1)}</strong></TableCell>}
                      {!isMobile && <TableCell align="right" sx={{ p: 0.5, fontSize: '0.7rem' }}><strong>{totals.clicks}</strong></TableCell>}
                      <TableCell align="right" sx={{ p: 0.5, fontSize: '0.7rem' }}><strong>{totals.leads}</strong></TableCell>
                      <TableCell align="right" sx={{ p: 0.5, fontSize: '0.7rem' }}><strong>{totals.revenue.toFixed(1)}</strong></TableCell>
                      {!isTablet && <TableCell align="right" sx={{ p: 0.5, fontSize: '0.7rem' }}>-</TableCell>}
                      <TableCell align="right" sx={{ p: 0.5, fontSize: '0.7rem', color: totals.profit >= 0 ? 'success.main' : 'error.main' }}><strong>{totals.profit.toFixed(1)}</strong></TableCell>
                     {!isTablet && <TableCell align="right" sx={{ p: 0.5, fontSize: '0.7rem' }}><strong>{totals.opens || '-'}</strong></TableCell>}
                      {!isTablet && <TableCell align="right" sx={{ p: 0.5, fontSize: '0.7rem' }}><strong>{totals.mdClicks || '-'}</strong></TableCell>}
                      {!isTablet && <TableCell align="right" sx={{ p: 0.5, fontSize: '0.7rem' }}><strong>{totals.unsubs || '-'}</strong></TableCell>}
 </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Add Domain Dialog */}
      <Dialog open={domainDialogOpen} onClose={() => setDomainDialogOpen(false)} maxWidth="xs" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>Add Domain</DialogTitle>
        <DialogContent>
          <TextField fullWidth size="small" label="Domain" value={newDomain.domain} onChange={(e) => setNewDomain({ ...newDomain, domain: e.target.value })} placeholder="news.example.com" sx={{ mt: 1, mb: 2 }} />
          <TextField fullWidth size="small" label="Sender ID (Maildome)" type="number" required value={newDomain.sender_id} onChange={(e) => setNewDomain({ ...newDomain, sender_id: e.target.value })} placeholder="e.g., 123" sx={{ mb: 2 }} />
          <FormControl fullWidth size="small">
            <InputLabel>Virtual List</InputLabel>
            <Select value={newDomain.virtual_list_id} onChange={(e) => setNewDomain({ ...newDomain, virtual_list_id: e.target.value })} label="Virtual List">
              <MenuItem value="">None</MenuItem>
              {database.virtual_lists?.map(vl => <MenuItem key={vl.id} value={vl.id}>{vl.name}</MenuItem>)}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDomainDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddDomain} disabled={!newDomain.sender_id}>Add</Button>
        </DialogActions>
      </Dialog>

      {/* Add VL Dialog */}
      <Dialog open={vlDialogOpen} onClose={() => setVlDialogOpen(false)} maxWidth="xs" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>Add Virtual List</DialogTitle>
        <DialogContent>
          <TextField fullWidth size="small" label="Name" value={newVl.name} onChange={(e) => setNewVl({ ...newVl, name: e.target.value })} placeholder="e.g., Openers" sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setVlDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddVl}>Add</Button>
        </DialogActions>
      </Dialog>

      {/* VL Settings Dialog */}
      <Dialog open={vlMaildomeDialogOpen} onClose={() => setVlMaildomeDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>Edit Virtual List</DialogTitle>
        <DialogContent>
          <TextField fullWidth size="small" label="VL Name" value={editVlName} onChange={(e) => setEditVlName(e.target.value)} sx={{ mt: 2, mb: 3 }} />
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>Maildome VL IDs</Typography>
          <Box display="flex" gap={1} mb={2}>
            <TextField size="small" label="Add VL ID" type="number" value={newVlMaildomeId} onChange={(e) => setNewVlMaildomeId(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleAddVlMaildomeId()} placeholder="e.g., 1295" sx={{ flex: 1 }} />
            <Button variant="outlined" onClick={handleAddVlMaildomeId} size="small">Add</Button>
          </Box>
          <Box display="flex" flexWrap="wrap" gap={1} sx={{ minHeight: 40 }}>
            {vlMaildomeIds.length === 0 ? <Typography color="text.secondary" variant="body2">No IDs added</Typography> : vlMaildomeIds.map((vlId, index) => <Chip key={index} label={vlId} onDelete={() => handleRemoveVlMaildomeId(vlId)} color="primary" variant="outlined" size="small" />)}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setVlMaildomeDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveVlMaildomeIds}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Status Menu */}
      <Menu anchorEl={statusMenuAnchor} open={Boolean(statusMenuAnchor)} onClose={handleCloseStatusMenu}>
        <MenuItem onClick={() => handleUpdateStatus('pending')}><RadioButtonUnchecked sx={{ color: '#9e9e9e', fontSize: 18, mr: 1 }} /> Pending</MenuItem>
        <MenuItem onClick={() => handleUpdateStatus('planned')}><Check sx={{ color: '#4caf50', fontSize: 18, mr: 1 }} /> Planned</MenuItem>
        <MenuItem onClick={() => handleUpdateStatus('paused')}><HourglassEmpty sx={{ color: '#ff9800', fontSize: 18, mr: 1 }} /> Paused</MenuItem>
        <MenuItem onClick={() => handleUpdateStatus('aborted')}><Cancel sx={{ color: '#f44336', fontSize: 18, mr: 1 }} /> Aborted</MenuItem>
      </Menu>

      {/* Import Opt-out Dialog */}
      <Dialog open={optoutDialogOpen} onClose={() => setOptoutDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Import Opt-out Contacts</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Paste emails (one per line, or separated by comma/semicolon). They will be added to Maildome with tracking_preference: off.
          </Typography>
          <TextField
            fullWidth multiline rows={8} size="small"
            placeholder={"email1@example.com\nemail2@example.com"}
            value={optoutText}
            onChange={(e) => setOptoutText(e.target.value)}
          />
          {optoutResult && !optoutResult.error && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Done: {optoutResult.processed} imported, {optoutResult.failed} failed, {optoutResult.skipped} skipped
              {optoutResult.failed_details?.length > 0 && (
                <Box mt={1}>{optoutResult.failed_details.map((f, i) => <Typography key={i} variant="caption" display="block">{f.email}: {f.reason}</Typography>)}</Box>
              )}
            </Alert>
          )}
          {optoutResult?.error && <Alert severity="error" sx={{ mt: 2 }}>{optoutResult.error}</Alert>}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOptoutDialogOpen(false)}>Close</Button>
          <Button variant="contained" color="warning" onClick={handleImportOptout} disabled={optoutLoading || !optoutText.trim()}>
            {optoutLoading ? 'Importing...' : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
export default DemDatabaseDetail;
